import React, { useState, useEffect, useRef } from "react";
import { Input, Button, Icon, Tooltip } from "antd";
import MapAIChatbotHolder from "../styles/mapAIChatbotStyle";
import botAvatar from "../assets/robot latinad (1).png";
import ProposalCard from "./ProposalCard";
import { EXAMPLES, ACTIVE_EXAMPLE } from "../services/examples";

const QUICK_ACTIONS = [
  { key: "add",      labelKey: "Add screens" },
  { key: "remove",   labelKey: "Remove screens" },
  { key: "summary",  labelKey: "Summarize cart screens" },
  { key: "budget",   labelKey: "Change budget" },
  { key: "campaign", labelKey: "Change campaign data" },
];

export default function MapAIChatbot({
  // modal control
  visible,
  onClose,
  t,
  // Redux state (from ChatbotContainer)
  sessionId,
  messages,
  currentProposalId,
  suggestReset,
  sendingMessage,
  confirmingProposal,
  chatbotError,
  campaignId,
  userId,
  // Redux dispatch (from ChatbotContainer)
  onInitSession,
  onSendMessage,
  onConfirmProposal,
  onResetSession,
}) {
  const [input, setInput] = useState('');
  const listRef = useRef(null);

  const [floatingParticles] = useState(() =>
    [...Array(18)].map((_, i) => ({
      left:           `${Math.random() * 100}%`,
      top:            `${Math.random() * 100}%`,
      animationDelay: `${i * 0.4}s`,
    }))
  );

  // Initialize session once when component first becomes visible
  useEffect(() => {
    if (visible && !sessionId && onInitSession) {
      onInitSession(Math.floor(Math.random() * 1e9) + 1);
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, sendingMessage]);

  // On (re)open, jump straight to the latest message. The modal uses
  // destroyOnClose, so the messages list is remounted scrolled to the top;
  // defer to the next frame so its content is laid out before we scroll.
  useEffect(() => {
    if (!visible) return undefined;
    const raf = requestAnimationFrame(() => {
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [visible]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || sendingMessage) return;
    setInput('');
    onSendMessage(campaignId, userId, trimmed);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleConfirmProposal = (action, confirmed, checkedIds) => {
    onConfirmProposal(action, confirmed, checkedIds, userId);
  };

  const handleRejectProposal = action => {
    onConfirmProposal(action, false, null, userId);
  };

  const isTyping = sendingMessage || confirmingProposal;

  /* ── TEST: respuesta hardcodeada (ver ./examples.js) ──
     Se muestra por defecto mientras no haya conversación real ni propuesta.
     Poné ACTIVE_EXAMPLE = null en examples.js para desactivar. */
  const example = ACTIVE_EXAMPLE ? EXAMPLES[ACTIVE_EXAMPLE] : null;

  const messagesToShow = !!example
    ? [{
        id: "example-msg",
        role: "assistant",
        text: example.message,
        responseType: example.response_type,
        proposal: example.proposal || null,
        proposalId: example.proposal_id || null,
        resolved: false,
      }]
    : messages;

  // Only the active proposal (the latest, still-unresolved one) is interactive.
  // Superseded or already-acted proposals stay in the timeline but disabled.
  const activeProposalId = example ? example.proposal_id : currentProposalId;
  const suggestResetToShow = suggestReset || (!!example && !!example.suggest_reset);

  return (
    <MapAIChatbotHolder
      visible={visible}
      onCancel={onClose}
      footer={null}
      closable={false}
      destroyOnClose
      width={window.innerWidth > 768 ? 480 : "92%"}
    >
      <div className="map-ai-chatbot">
        <div className="chatbot-particles">
          {floatingParticles.map((particle, i) => (
            <div
              key={`chatbot-particle-${i}`}
              className="chatbot-float-particle"
              style={{
                left:           particle.left,
                top:            particle.top,
                animationDelay: particle.animationDelay,
              }}
            />
          ))}
        </div>

        <div className="chatbot-header">
          <div className="chatbot-avatar">
            <img src={botAvatar} alt={t("AI Planner")} className="chatbot-avatar-icon" />
          </div>
          <div className="chatbot-header-text">
            <span className="chatbot-title">{t("AI Planner")}</span>
            <span className="chatbot-subtitle">{t("ChatbotSubtitle")}</span>
          </div>
          <div className="chatbot-header-actions">
            <Tooltip title={t('ChatbotReset')}>
              <span>
                <Button
                  shape="rounded"
                  size="small"
                  className="chatbot-header-btn"
                  onClick={onResetSession}
                  disabled={isTyping}
                >
                  <Icon type="reload" />
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={t('Close')}>
              <span>
                <Button
                  shape="rounded"
                  size="small"
                  className="chatbot-header-btn"
                  onClick={onClose}
                >
                  <Icon type="close" />
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>

        <div className="chatbot-messages" ref={listRef}>
          {messagesToShow.map((message) => {
            if (message.responseType === "session_reset") {
              return (
                <div
                  key={message.id}
                  className="chatbot-bubble chatbot-bubble--system"
                >
                  <Icon type="info-circle" /> {t("ChatbotSessionExpired")}
                </div>
              );
            }
            return (
              <React.Fragment key={message.id}>
                {message.text ? (
                  <div
                    className={
                      message.role === "user"
                        ? "chatbot-bubble chatbot-bubble--user"
                        : "chatbot-bubble chatbot-bubble--assistant"
                    }
                  >
                    {message.text}
                  </div>
                ) : null}

                {message.proposal && (
                  <ProposalCard
                    proposal={message.proposal}
                    action={message.proposal.action}
                    disabled={message.resolved || message.proposalId !== activeProposalId}
                    onConfirm={handleConfirmProposal}
                    onReject={handleRejectProposal}
                    loading={confirmingProposal}
                    t={t}
                  />
                )}
              </React.Fragment>
            );
          })}

          {isTyping && (
            <div className="chatbot-bubble chatbot-bubble--assistant chatbot-typing">
              <span className="chatbot-dot" />
              <span className="chatbot-dot" />
              <span className="chatbot-dot" />
            </div>
          )}

          {chatbotError && (
            <div className="chatbot-bubble chatbot-bubble--error">
              {typeof chatbotError === 'string' && chatbotError !== 'Error'
                ? chatbotError
                : t('ChatbotError')}
            </div>
          )}
        </div>

        {!messages.some((message) => message.role === "user") && (
          <div className="chatbot-quick-actions">
            {QUICK_ACTIONS.map((action) => (
              <button
                key={action.key}
                type="button"
                className="chatbot-chip"
                onClick={() => setInput(t(action.labelKey))}
              >
                {t(action.labelKey)}
              </button>
            ))}
          </div>
        )}

        {suggestResetToShow && (
          <div className="chatbot-suggest-reset">
            <Icon type="warning" />
            {t('ChatbotResetWarning')}
            <Button size="small" onClick={onResetSession}>
              {t('ChatbotReset')}
            </Button>
          </div>
        )}
        <div className="chatbot-input-row">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("Type a message")}
            className="chatbot-input"
          />
          <Button
            type="primary"
            className="chatbot-send-button"
            onClick={handleSend}
            disabled={!input.trim() || isTyping}
          >
            <Icon type="arrow-up" />
          </Button>
        </div>
      </div>
    </MapAIChatbotHolder>
  );
}
